import { type ReactNode } from 'react';

import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { type PageLayoutAlertProps } from './PageLayoutAlert';
import PageLayoutContent from './PageLayoutContent';
import PageLayoutHeader, {
	type PageLayoutHeaderProps,
} from './PageLayoutHeader';
import PageLayoutNote, { type PageLayoutNoteProps } from './PageLayoutNote';

export interface PageLayoutSimpleProps extends StackProps {
	actionsSlot?: PageLayoutHeaderProps['actionsSlot'];
	/** Alert content to display above the title (dismissable alerts, warnings, etc.) */
	alert?: PageLayoutAlertProps['children'];
	attributesSlot?: PageLayoutHeaderProps['attributesSlot'];
	breadcrumbsSlot?: PageLayoutHeaderProps['breadcrumbsSlot'];
	children: ReactNode;
	description?: PageLayoutHeaderProps['description'];
	Icon?: PageLayoutHeaderProps['Icon'];
	iconBoxProps?: PageLayoutHeaderProps['iconBoxProps'];
	isLoading?: PageLayoutHeaderProps['isLoading'];
	isTitleSaveBusy?: PageLayoutHeaderProps['isTitleSaveBusy'];
	note?: PageLayoutNoteProps['children'];
	noteProps?: PageLayoutNoteProps['noteProps'];
	onTitleSave?: PageLayoutHeaderProps['onTitleSave'];
	title: PageLayoutHeaderProps['title'];
	pageLayoutContentProps?: StackProps;
}

const PageLayoutSimple = ({
	actionsSlot,
	alert,
	attributesSlot,
	breadcrumbsSlot,
	children,
	description,
	Icon,
	iconBoxProps,
	isLoading,
	isTitleSaveBusy,
	note,
	noteProps,
	onTitleSave,
	title,
	sx,
	pageLayoutContentProps,
	...rest
}: PageLayoutSimpleProps) => {
	return (
		<Stack
			sx={combineSx(
				{
					position: 'relative',
					flexGrow: 1,
				},
				sx,
			)}
			{...rest}
		>
			{note && <PageLayoutNote noteProps={noteProps}>{note}</PageLayoutNote>}

			<PageLayoutHeader
				actionsSlot={actionsSlot}
				alert={alert}
				attributesSlot={attributesSlot}
				breadcrumbsSlot={breadcrumbsSlot}
				description={description}
				Icon={Icon}
				iconBoxProps={iconBoxProps}
				isLoading={isLoading}
				isTitleSaveBusy={isTitleSaveBusy}
				onTitleSave={onTitleSave}
				title={title}
			/>

			<PageLayoutContent
				{...pageLayoutContentProps}
				sx={combineSx({ flexGrow: 1 }, pageLayoutContentProps?.sx)}
			>
				{children}
			</PageLayoutContent>
		</Stack>
	);
};

export default PageLayoutSimple;
